import React, { useState } from 'react';
import { Card, Form, Select, Switch, InputNumber, Button, Divider, Typography, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ClearOutlined, DownloadOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons';
import localforage from 'localforage';

const { Title, Text } = Typography;
const { Option } = Select;

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [clearingCache, setClearingCache] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [importingData, setImportingData] = useState(false);

  // 保存設置
  const handleSave = async (values: {
    language: string;
    theme: string;
    offlineMode: boolean;
    syncInterval: number;
  }) => {
    try {
      await localforage.setItem('settings', values);

      // 更新語言
      if (values.language !== i18n.language) {
        i18n.changeLanguage(values.language);
      }

      message.success(t('common.success'));
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error(t('common.error'));
    }
  };

  // 清除緩存
  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      // 清除所有緩存，但保留設置
      const settings = await localforage.getItem('settings');
      await localforage.clear();
      if (settings) {
        await localforage.setItem('settings', settings);
      }
      message.success(t('common.success'));
    } catch (error) {
      console.error('Error clearing cache:', error);
      message.error(t('common.error'));
    } finally {
      setClearingCache(false);
    }
  };

  // 導出數據
  const handleExportData = async () => {
    setExportingData(true);
    try {
      // 獲取所有數據
      const keys = await localforage.keys();
      const data: Record<string, any> = {};

      for (const key of keys) {
        data[key] = await localforage.getItem(key);
      }

      // 創建下載文件
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ecommerce-system-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      message.success(t('common.success'));
    } catch (error) {
      console.error('Error exporting data:', error);
      message.error(t('common.error'));
    } finally {
      setExportingData(false);
    }
  };

  // 導入數據
  const handleImportData = () => {
    setImportingData(true);

    // 創建文件輸入元素
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';

    input.onchange = async (e: Event) => {
      try {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (!file) {
          setImportingData(false);
          return;
        }

        const reader = new FileReader();
        reader.onload = async (event: ProgressEvent<FileReader>) => {
          try {
            if (!event.target?.result) {
              throw new Error('Failed to read file');
            }
            const data = JSON.parse(event.target.result as string);

            // 導入數據
            for (const [key, value] of Object.entries(data)) {
              await localforage.setItem(key, value);
            }

            message.success(t('common.success'));
            // 重新加載頁面以應用導入的設置
            window.location.reload();
          } catch (error) {
            console.error('Error parsing import file:', error);
            message.error(t('common.error'));
            setImportingData(false);
          }
        };

        reader.onerror = () => {
          message.error(t('common.error'));
          setImportingData(false);
        };

        reader.readAsText(file);
      } catch (error) {
        console.error('Error importing data:', error);
        message.error(t('common.error'));
        setImportingData(false);
      }
    };

    input.click();
  };

  // 加載設置
  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await localforage.getItem('settings');
        if (settings) {
          form.setFieldsValue(settings);
        } else {
          // 默認設置
          form.setFieldsValue({
            language: i18n.language,
            theme: 'light',
            offlineMode: false,
            syncInterval: 30,
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
  }, [form, i18n.language]);

  return (
    <div>
      <Title level={2}>{t('settings.title')}</Title>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            language: 'zh-TW',
            theme: 'light',
            offlineMode: false,
            syncInterval: 30,
          }}
        >
          <Form.Item
            name="language"
            label={t('settings.language')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="zh-TW">繁體中文</Option>
              <Option value="en-US">English</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="theme"
            label={t('settings.theme')}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="light">Light</Option>
              <Option value="dark">Dark</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="offlineMode"
            label={t('settings.offlineMode')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="syncInterval"
            label={t('settings.syncInterval')}
            rules={[{ required: true }]}
          >
            <InputNumber min={5} max={120} style={{ width: '100%' }} addonAfter="秒" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
            >
              {t('common.save')}
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={4}>{t('settings.clearCache')}</Title>
          <Text type="secondary">
            {t('common.warning')}: {t('settings.clearCache')}
          </Text>
          <Button
            danger
            icon={<ClearOutlined />}
            onClick={handleClearCache}
            loading={clearingCache}
          >
            {t('settings.clearCache')}
          </Button>

          <Divider />

          <Title level={4}>{t('settings.exportData')}</Title>
          <Text type="secondary">
            {t('settings.exportData')}
          </Text>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportData}
            loading={exportingData}
          >
            {t('settings.exportData')}
          </Button>

          <Divider />

          <Title level={4}>{t('settings.importData')}</Title>
          <Text type="secondary">
            {t('common.warning')}: {t('settings.importData')}
          </Text>
          <Button
            icon={<UploadOutlined />}
            onClick={handleImportData}
            loading={importingData}
          >
            {t('settings.importData')}
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default Settings;
